import { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useWeb3React } from '@web3-react/core';
import { Web3Provider } from '@ethersproject/providers';
import { Contract, ethers } from 'ethers';
import { motion } from 'framer-motion';
import TnCGameBallStats from '../components/TnCGame/TnCGameBallStats';
import TnCGamePlayers from '../components/TnCGame/TnCGamePlayers';
import TnCGameSchema from '../components/TnCGame/TnCGameSchema';
import TnCGameTotalPrize from '../components/TnCGame/TnCGameTotalPrize';
import TnCGGameScores from '../components/TnCGame/TnCGGameScores';
import TnCGameEventsLog from '../components/TnCGame/TnCGameEventsLog';
import TnCInfoCard from '../components/TnCInfo/TnCInfoCard';
import TnCGameUIButton from '../components/TnCGame/TnCGameUIButton';
import TnCGameLeaveButton from '../components/TnCGame/TnCGameLeaveButton';
import TnCTitle from '../components/Graphics/TnCTitle';
import managerABI from '../contracts/tncGame/managerv1/ABI';

interface ITncEventLog {
  time: number;
  text: string;
}

const TnCGame = () => {
  // contract definition
  const location = useLocation();
  const path = location.pathname.split('/')[2];

  const { library } = useWeb3React<Web3Provider>();

  const provider = new ethers.providers.StaticJsonRpcProvider(
    'https://data-seed-prebsc-1-s1.binance.org:8545/'
  );

  const managerAddress = process.env.REACT_APP_TNCGAME_MANAGER_ADDRESS;
  const contract = new Contract(
    `${managerAddress}`,
    managerABI.ABI,
    library?.getSigner() ? library?.getSigner() : provider
  );

  // states and contexts
  const [tncEventsLog, setTncEventsLog] = useState<ITncEventLog[]>([]);
  const [velocity, setVelocity] = useState<number>(0);
  const [fieldLength, setFieldLength] = useState<number>(100000);
  const [winningScore, setWinningScore] = useState<number>(3);
  const [currentBlock, setCurrentBlock] = useState<number>(0);
  const [kickedBlock, setKickedBlock] = useState<number>(0);
  const [player1, setPlayer1] = useState<string>(ethers.constants.AddressZero);
  const [player2, setPlayer2] = useState<string>(ethers.constants.AddressZero);
  const [score1, setScore1] = useState<number>(0);
  const [score2, setScore2] = useState<number>(0);
  const [gameStatus, setGameStatus] = useState<number>(0);
  const [kickerIndex, setKickerIndex] = useState<number>(0);
  const [gameBalance, setGameBalance] = useState<string>('0');
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [isCollectable, setIsCollectable] = useState<boolean>(false);
  const [isCatcherTimeout, setIsCatcherTimeout] = useState<boolean>(false);

  // fetching game info
  const getPeriodicGameInfo = async () => {
    try {
      const gameInfo = await contract.getGameInfo(path);
      const fetchedFieldLength = gameInfo[4];
      setFieldLength(fetchedFieldLength.toNumber());
      const fetchedWinningScore = gameInfo[5];
      setWinningScore(fetchedWinningScore.toNumber());
      const fetchedKickedBlock = gameInfo[10];
      setKickedBlock(fetchedKickedBlock.toNumber());
      const fetchedVelocity = gameInfo[11];
      setVelocity(fetchedVelocity.toNumber());
      const players = gameInfo[0];
      setPlayer1(players[0]);
      setPlayer2(players[1]);
      const fetchedGameStatus = gameInfo[2];
      const scores = await gameInfo[1];
      setScore1(scores[0].toNumber());
      setScore2(scores[1].toNumber());
      setGameStatus(fetchedGameStatus);
      const fetchedKickerIndex = gameInfo[7];
      setKickerIndex(fetchedKickerIndex.toNumber());
      const fetchedGameBalance = gameInfo[8];
      const parsedGameBalance = ethers.utils.formatEther(fetchedGameBalance);
      setGameBalance(parsedGameBalance);
      const fetchedIsCollable = await contract.isCollectable(path);
      setIsCollectable(fetchedIsCollable);
      const fetchedIsCatcherTimeout = await contract.isCatcherTimeout(path);
      setIsCatcherTimeout(fetchedIsCatcherTimeout);
      setIsFetching(false);
    } catch (error) {
      console.error(error);
    }
  };

  // cleanup states on unmount
  const cleanup = () => {
    setFieldLength(100000);
    setWinningScore(3);
    setCurrentBlock(0);
    setVelocity(0);
    setKickedBlock(0);
    setPlayer1(ethers.constants.AddressZero);
    setPlayer2(ethers.constants.AddressZero);
    setScore1(0);
    setScore2(0);
    setGameStatus(0);
    setKickerIndex(0);
    setGameBalance('0');
    setIsFetching(true);
    setIsCollectable(false);
    setIsCatcherTimeout(false);
  };

  // lifecycle hooks
  useEffect(() => {
    provider.on('block', (blockNumber) => {
      setCurrentBlock(blockNumber);
      getPeriodicGameInfo();
    });
    return () => {
      provider.off('block', (blockNumber) => {
        setCurrentBlock(blockNumber);
        getPeriodicGameInfo();
      });
      cleanup();
    };
  }, []);

  return (
    <motion.div
      key="TnCGame"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="card">
        <div className="cardTitle">
          <TnCTitle />
        </div>
        <div className="innerCard">
          <div className="innerCardTitle innerCardTitleWithButton">
            <span>Gaming Arena</span>
            <TnCGameLeaveButton
              player1={player1}
              player2={player2}
              contract={contract}
            />
          </div>
          <div className="tncGameScoresWrapper">
            <TnCGGameScores score1={score1} score2={score2} />
          </div>
          <div className="tncGameSchemaWrapper">
            <TnCGameSchema
              fieldLength={fieldLength}
              currentBlock={currentBlock}
              gameStatus={gameStatus}
              kickerIndex={kickerIndex}
              kickedBlock={kickedBlock}
              velocity={velocity}
            />
          </div>
          <div className="tncGamePlayersWrapper">
            <TnCGamePlayers player1={player1} player2={player2} />
          </div>
          <TnCGameUIButton
            player1={player1}
            player2={player2}
            gameStatus={gameStatus}
            kickerIndex={kickerIndex}
            velocity={velocity}
            isCollectable={isCollectable}
            isCatcherTimeout={isCatcherTimeout}
            isFetching={isFetching}
            contract={contract}
          />
        </div>
        <div className="innerCard">
          <div className="tncGameSchemaWrapper">
            <TnCGameTotalPrize gameBalance={gameBalance} />
          </div>
        </div>
        <div className="innerCard">
          <div className="innerCardTitle">Ball Stats</div>
          <TnCGameBallStats
            velocity={velocity}
            fieldLength={fieldLength}
            winningScore={winningScore}
            currentBlock={currentBlock}
            kickedBlock={kickedBlock}
          />
        </div>
        <div className="innerCard" style={{ height: '200px' }}>
          <div className="innerCardTitle">Events Log</div>
          <TnCGameEventsLog
            tncEventsLog={tncEventsLog}
            setTncEventsLog={setTncEventsLog}
          />
        </div>
      </div>
      <TnCInfoCard />
    </motion.div>
  );
};

export default TnCGame;
